import React, { createContext, useContext } from 'react';
import { useOptionalAuditYear } from '../../contexts/AuditYearContext.tsx';
import type { AnalyticsScope } from '../../request/analytics/scope.ts';

/**
 * Lets a subtree pin the analytics scope instead of following the globally
 * selected audit year. The widget gallery uses this to render previews
 * against seeded sample data (scope `{ auditYearId: null }`); a dashboard
 * tile switched to Demo uses it to request demo data.
 */
const AnalyticsScopeOverrideContext = createContext<AnalyticsScope | undefined>(undefined);

export const AnalyticsScopeOverride: React.FC<{ scope: AnalyticsScope; children: React.ReactNode }> = ({
  scope,
  children,
}) => <AnalyticsScopeOverrideContext.Provider value={scope}>{children}</AnalyticsScopeOverrideContext.Provider>;

/** Scope every analytics hook passes to its fetcher and uses in its query key (via `scopeKey`). */
export function useAnalyticsScope(): Required<AnalyticsScope> {
  const override = useContext(AnalyticsScopeOverrideContext);
  const auditYear = useOptionalAuditYear();
  if (override) {
    return {
      auditYearId: override.auditYearId ?? null,
      dataMode: override.dataMode ?? 'live',
      demoNonce: override.demoNonce ?? '',
    };
  }
  return { auditYearId: auditYear?.selectedAuditYearId ?? null, dataMode: 'live', demoNonce: '' };
}
